import React, { useMemo } from 'react';
import theme from './RoomSelector.module.scss';
import { RoomDto } from '@/types';
import { Checkbox, SelectionList } from '@ygtechnologies/rubus-ui';
import { checkSubsetSelection, toggleArrayValues } from '@/utils/arrays';
import { LoadingIndicator } from '@ygtechnologies/bygr-frontend-component-library';
import { Trans } from '@lingui/react/macro';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { useRooms } from '@/services/rooms';
import RoomLabel from '@/components/RoomLabel';

export interface RoomSelectorProps {
    selectedRoomIds: number[];
    onChange(newValue: number[]): void;
    projectId: number;
    filter?(unit: RoomDto): boolean;
    /**
     * Rooms that can't be toggled: their checkbox is disabled and they keep their current selection state
     * (bulk select-all never adds or removes them). Used by the unpublish dialog for ordered rooms.
     */
    lockedRoomIds?: number[];
}

export const RoomSelector: React.FC<RoomSelectorProps> = ({ projectId, selectedRoomIds, onChange, filter, lockedRoomIds }) => {
    const { data: rooms } = useRooms(projectId);
    const lockedSet = useMemo(() => new Set(lockedRoomIds ?? []), [lockedRoomIds]);

    const handleChange = (roomIds: number[], flag: boolean) => {
        // Leave locked rooms at their current state; bulk select-all must not toggle them.
        const toggleable = roomIds.filter((id) => !lockedSet.has(id));
        onChange(toggleArrayValues(selectedRoomIds, toggleable, flag));
    };

    const filteredRooms = useMemo(() => {
        if (!rooms) {
            return null;
        }

        let data = rooms;

        if (filter) {
            data = rooms.filter(filter);
        }

        return data;
    }, [rooms, filter]);

    if (!filteredRooms) {
        return <LoadingIndicator />;
    }

    // Locked rooms can't be toggled, so "select all" reflects/operates on the selectable ones only -
    // otherwise a list containing a locked room could never reach the 'all' state.
    const selectableRoomIds = filteredRooms.filter((x) => !lockedSet.has(x.id)).map((x) => x.id);
    const allSelectionState = checkSubsetSelection(selectedRoomIds, selectableRoomIds);

    return (
        <div className={theme.container}>
            <Checkbox
                className={theme.selectAll}
                inputSize="md"
                checked={allSelectionState === 'all'}
                indeterminate={allSelectionState === 'some'}
                onChange={(e) => handleChange(selectableRoomIds, e.target.checked)}>
                <Trans>Select all</Trans>
            </Checkbox>
            <SelectionList selectionMode="multi">
                {filteredRooms.map((room) => (
                    <SelectionList.Item
                        key={room.id}
                        label={
                            <span className={theme.roomLabel}>
                                {room.icon && <FontAwesomeIcon icon={['fal', room.icon]} fixedWidth />}
                                <RoomLabel name={room.name} labelNote={room.labelNote} />
                                {lockedSet.has(room.id) && (
                                    <span className={theme.lockedHint}>
                                        <Trans>Ordered</Trans>
                                    </span>
                                )}
                            </span>
                        }
                        disabled={lockedSet.has(room.id)}
                        selected={selectedRoomIds.includes(room.id)}
                        onChange={(checked) => handleChange([room.id], checked)}
                    />
                ))}
            </SelectionList>
        </div>
    );
};
